/**
 * Turns the server's usage summary into what the Usage Analytics screen shows (HRMS-322).
 */

// The role filter's "everyone" value. @since NEXT_VERSION
export const ALL_ROLES_FILTER = "ALL";

const DAY_MS = 24 * 60 * 60 * 1000;
const NO_USE = { uses: 0, users: 0 };

// Active people holding at least one of the roles; grouped by role combination so nobody counts twice.
const activePeopleWithAnyRole = (summary, roles) =>
  (summary?.activeUsersByRoleSet ?? [])
    .filter((group) => group.roles.some((role) => roles.includes(role)))
    .reduce((total, group) => total + group.users, 0);

/**
 * Every feature the role can reach, most used first, merged with the summary so a feature nobody
 * used still shows, at zero. Adoption is the share of active people who can reach it who used it.
 *
 * @since NEXT_VERSION
 */
export const rankFeatures = (catalog, summary, role) => {
  const everyone = role === ALL_ROLES_FILTER;
  const usageByKey = new Map((summary?.features ?? []).map((feature) => [feature.feature, feature]));

  return catalog
    .filter((feature) => everyone || feature.roles.includes(role))
    .map((feature) => {
      const usage = usageByKey.get(feature.key);
      const counts = everyone ? usage ?? NO_USE : usage?.byRole?.[role] ?? NO_USE;
      const reachable = everyone
        ? activePeopleWithAnyRole(summary, feature.roles)
        : summary?.activeUsersByRole?.[role] ?? 0;
      return {
        key: feature.key,
        label: feature.label,
        kind: feature.kind,
        uses: counts.uses,
        people: counts.users,
        // Capped: the feature list's roles are a close match to real access, not an exact one.
        adoption: reachable > 0 ? Math.min(1, counts.users / reachable) : null,
        avgDurationMs: usage?.avgDurationMs ?? null,
      };
    })
    .sort((a, b) => b.uses - a.uses || b.people - a.people || a.label.localeCompare(b.label));
};

/**
 * One entry per day from `from` to `to` (both "yyyy-mm-dd"), zero where the server listed none,
 * so a quiet day shows as a dip rather than vanishing from the line.
 *
 * @since NEXT_VERSION
 */
export const everyDay = (from, to, daily) => {
  const usersByDay = new Map((daily ?? []).map((entry) => [entry.day, entry.users]));
  const days = [];
  // Stepped in UTC so no local clock change can skip or repeat a day.
  for (let ms = Date.parse(`${from}T00:00:00Z`); ms <= Date.parse(`${to}T00:00:00Z`); ms += DAY_MS) {
    const day = new Date(ms).toISOString().slice(0, 10);
    days.push({ day, users: usersByDay.get(day) ?? 0 });
  }
  return days;
};

const localDay = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/**
 * The last `days` calendar days up to and including `today`, as "yyyy-mm-dd" dates.
 *
 * @since NEXT_VERSION
 */
export const periodEnding = (days, today = new Date()) => {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (days - 1));
  return { from: localDay(start), to: localDay(today) };
};
